import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion as m } from 'framer-motion';
import { FiArrowLeft, FiX, FiChevronLeft, FiChevronRight, FiPlus } from 'react-icons/fi';

const ModalOverlay = styled(m.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgb(236, 233, 233);
  z-index: 1000;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  font-family: 'Poppins', sans-serif;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  padding: 15px;
  background: #f8f8f8;
  position: sticky;
  top: 0;
  z-index: 1;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
`;

const BackButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  color: #333;
  padding: 8px;
`;

const HeaderTitle = styled.span`
  font-size: 16px;
  color: #333;
  margin-left: 8px;
  font-weight: 600;
  font-family: 'Poppins', sans-serif;
`;

const ImageGallery = styled.div`
  display: flex;
  gap: 15px;
  padding: 20px;
  background: #f8f8f8;
  justify-content: center;
  align-items: center;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  
  &::-webkit-scrollbar {
    display: none;
  }
`;

const ImageLink = styled.a`
  text-decoration: none;
  color: inherit;
  display: block;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.05);
  }
`;

const ProductImage = styled.img`
  width: 110px;
  height: 110px;
  object-fit: cover;
  border-radius: 12px;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const Content = styled.div`
  padding: 20px;
  flex: 1;
  overflow-y: auto;
  position: relative;
  z-index: 1;
  padding-bottom: 80px;
`;

const Section = styled.div`
  margin-bottom: 25px;
  background: white;
  border-radius: 12px;
  overflow: visible;
  border: 1px solid rgba(207, 49, 49, 0.1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  position: relative;
`;

const SectionTitle = styled.h3<{ color?: string }>`
  font-size: 14px;
  color: ${props => props.color || '#ff0000'};
  margin-bottom: 0;
  font-weight: 600;
  font-family: 'Poppins', sans-serif;
  letter-spacing: 0.5px;
  padding: 15px;
  background: rgba(255, 0, 0, 0.03);
  border-bottom: 1px solid rgba(207, 49, 49, 0.1);
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
  padding: 15px;
  background: white;
  position: relative;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const InfoRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: #f8f8f8;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
  position: relative;
  
  &:hover {
    border-color: #ff0000;
    box-shadow: 0 2px 8px rgba(255, 0, 0, 0.1);
  }
`;

const InfoLabel = styled.span`
  font-size: 12px;
  color: rgb(250, 154, 154);
  font-weight: 600;
  font-family: 'Poppins', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const InfoValue = styled.span`
  font-size: 13px;
  color: #333;
  min-height: 20px;
  display: flex;
  align-items: center;
  font-family: 'Poppins', sans-serif;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  padding: 15px;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  z-index: 1001;
  max-width: 100%;
`;

const Button = styled(m.button)<{ variant?: 'primary' | 'secondary' }>`
  flex: 1;
  padding: 14px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  background: ${props => props.variant === 'primary' ? '#ff0000' : '#f8f8f8'};
  color: ${props => props.variant === 'primary' ? 'white' : '#333'};
  font-family: 'Poppins', sans-serif;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
  }
`;

const FullScreenImage = styled(m.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
`;

const FullScreenImageContent = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NavigationControls = styled.div`
  position: absolute;
  top: 180px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 20px;
  z-index: 2001;
`;

const NavigationButton = styled(m.button)`
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  padding: 12px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(5px);
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }

  @media (max-width: 768px) {
    padding: 10px;
    font-size: 18px;
  }
`;

const FullScreenImageView = styled(m.img)`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

const FullScreenImageClose = styled(m.button)`
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  padding: 12px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(5px);
  z-index: 2001;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }

  @media (max-width: 768px) {
    padding: 10px;
    font-size: 20px;
  }
`;

const StockInfoSelect = styled.select`
  width: 100%;
  padding: 8px 12px;
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  font-size: 13px;
  color: #333;
  outline: none;
  font-family: 'Poppins', sans-serif;
  
  &:focus {
    border-color: #ff0000;
    box-shadow: 0 2px 8px rgba(255, 0, 0, 0.1);
  }
`;

const CustomSelect = styled.div`
  position: relative;
  width: 100%;
  z-index: 1000;
`;

const SelectInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  font-size: 13px;
  color: #333;
  outline: none;
  font-family: 'Poppins', sans-serif;
  
  &:focus {
    border-color: #ff0000;
    box-shadow: 0 2px 8px rgba(255, 0, 0, 0.1);
  }
`;

const DropdownList = styled.ul<{ show: boolean }>`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  width: 100%;
  max-height: 200px;
  overflow-y: auto;
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 9999;
  display: ${props => props.show ? 'block' : 'none'};
  padding: 0;
  list-style: none;
  margin: 0;
`;

const DropdownItem = styled.li`
  padding: 10px 12px;
  cursor: pointer;
  font-size: 13px;
  color: #333;
  list-style: none;
  background: white;
  
  &:hover {
    background: #f8f8f8;
  }

  &.add-new {
    color: #ff0000;
    display: flex;
    align-items: center;
    gap: 8px;
    border-top: 1px solid rgba(0, 0, 0, 0.05);
  }
`;

interface ViewProductProps {
  onClose: () => void;
  onShortlist: () => void;
  productData: {
    code: string;
    images: string[];
    details: {
      technical: {
        material: string;
        colorTexture: string;
        variation: string;
        repetition: string;
        recommendation: string;
        traffic: string;
        edgean: string;
        spacer: string;
        recommendedAdhesive: string;
        recommendedGrout: string;
        antiSlip: string;
      };
      size: {
        length: string;
        width: string;
        tesseraSize: string;
        thickness: string;
        dia: string;
      };
      options: {
        colorOption: string;
        sizeOption: string;
      };
    };
  };
}

const SanitaryProductView: React.FC<ViewProductProps> = ({ onClose, onShortlist, productData }) => {
  const [roomName, setRoomName] = useState('');
  const [area, setArea] = useState('');
  const [showRoomDropdown, setShowRoomDropdown] = useState(false);
  const [roomOptions, setRoomOptions] = useState<string[]>([]);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right'>('right');
  const [areaOptions] = useState<string[]>([
    'Bathroom',
    'Kitchen',
    'Powder Room',
    'Laundry',
    'Outdoor',
    'Other'
  ]);

  useEffect(() => {
    // Load saved room names from localStorage
    const savedRooms = localStorage.getItem('roomNames');
    if (savedRooms) {
      setRoomOptions(JSON.parse(savedRooms));
    } else {
      setRoomOptions(['Karthik\'s room']);
    }
  }, []);

  const handleRoomSelect = (room: string) => {
    setRoomName(room);
    setShowRoomDropdown(false);
    
    if (!roomOptions.includes(room)) {
      const newOptions = [...roomOptions, room];
      setRoomOptions(newOptions);
      localStorage.setItem('roomNames', JSON.stringify(newOptions));
    }
  };

  // Add click outside handler to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showRoomDropdown) {
        const target = event.target as HTMLElement;
        if (!target.closest('.custom-select')) {
          setShowRoomDropdown(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showRoomDropdown]);

  const handlePrevImage = () => {
    setSlideDirection('left');
    setSelectedImageIndex((prev) => 
      prev === 0 ? productData.images.length - 1 : prev - 1
    );
  };

  const handleNextImage = () => {
    setSlideDirection('right');
    setSelectedImageIndex((prev) => 
      prev === productData.images.length - 1 ? 0 : prev + 1
    );
  };

  return (
    <ModalOverlay
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <Header>
        <BackButton onClick={onClose}>
          <FiArrowLeft />
        </BackButton>
        <HeaderTitle>Product Details</HeaderTitle>
      </Header>

      <ImageGallery>
        {productData.images.map((image, index) => (
          <ImageLink
            key={index}
            href="#"
            onClick={(e) => {
              e.preventDefault();
              setSelectedImage(image);
              setSelectedImageIndex(index);
            }}
          >
            <ProductImage src={image} alt={`Product ${index + 1}`} />
          </ImageLink>
        ))}
      </ImageGallery>

      <Content>
        <Section>
          <SectionTitle>ROOM INFO</SectionTitle>
          <InfoGrid>
            <InfoRow>
              <InfoLabel>Room Name</InfoLabel>
              <CustomSelect className="custom-select">
                <SelectInput
                  value={roomName}
                  onChange={(e) => setRoomName(e.target.value)}
                  onFocus={() => setShowRoomDropdown(true)}
                  placeholder="Enter room name"
                />
                <DropdownList show={showRoomDropdown}>
                  {roomOptions.map((room, index) => (
                    <DropdownItem
                      key={index}
                      onClick={() => handleRoomSelect(room)}
                    >
                      {room}
                    </DropdownItem>
                  ))}
                  {roomName && !roomOptions.includes(roomName) && (
                    <DropdownItem
                      className="add-new"
                      onClick={() => handleRoomSelect(roomName)}
                    >
                      <FiPlus /> Add "{roomName}"
                    </DropdownItem>
                  )}
                </DropdownList>
              </CustomSelect>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Area</InfoLabel>
              <StockInfoSelect
                value={area}
                onChange={(e) => setArea(e.target.value)}
              >
                <option value="">Select Area</option>
                {areaOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </StockInfoSelect>
            </InfoRow>
          </InfoGrid>
        </Section>

        <Section>
          <SectionTitle>PRODUCT INFO</SectionTitle>
          <InfoGrid>
            <InfoRow>
              <InfoLabel>Segment</InfoLabel>
              <InfoValue>Bathware & Bath Fittings</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Company</InfoLabel>
              <InfoValue>CATALANO</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Country</InfoLabel>
              <InfoValue>ITALY</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Company Item Code</InfoLabel>
              <InfoValue>160AGRLX00</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>System Code</InfoLabel>
              <InfoValue>6332</InfoValue>
            </InfoRow>
          </InfoGrid>
        </Section>

        <Section>
          <SectionTitle>ITEM DESCRIPTION</SectionTitle>
          <InfoGrid>
            <InfoRow style={{ gridColumn: '1 / -1' }}>
              <InfoValue>GREEN LUX COUNTER TOP / SEMI INSET WASH BASIN</InfoValue>
            </InfoRow>
          </InfoGrid>
        </Section>

        <Section>
          <SectionTitle>TECHNICAL INFO</SectionTitle>
          <InfoGrid>
            <InfoRow>
              <InfoLabel>Series</InfoLabel>
              <InfoValue>GREEN LUX</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Product Type</InfoLabel>
              <InfoValue>WASH BASIN</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Sub Product</InfoLabel>
              <InfoValue>COUNTER TOP/SEMI INSET</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Color</InfoLabel>
              <InfoValue>WHITE</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Material</InfoLabel>
              <InfoValue>VITREOUS CHINA</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Includes</InfoLabel>
              <InfoValue></InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Must Order</InfoLabel>
              <InfoValue></InfoValue>
            </InfoRow>
          </InfoGrid>
        </Section>

        <Section>
          <SectionTitle>DIMENSIONS</SectionTitle>
          <InfoGrid>
            <InfoRow>
              <InfoLabel>Length</InfoLabel>
              <InfoValue>600.00 mm</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Width</InfoLabel>
              <InfoValue>380.00</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Height</InfoLabel>
              <InfoValue>140.00</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Depth</InfoLabel>
              <InfoValue></InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Dia</InfoLabel>
              <InfoValue>0.00</InfoValue>
            </InfoRow>
          </InfoGrid>
        </Section>

        <Section>
          <SectionTitle>OPTIONS</SectionTitle>
          <InfoGrid>
            <InfoRow>
              <InfoLabel>Color Options</InfoLabel>
              <InfoValue>WHITE SATIN, GREY SATIN, BLUE SATIN, GR</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Size Options</InfoLabel>
              <InfoValue>650X400X140, 750X400X140</InfoValue>
            </InfoRow>
          </InfoGrid>
        </Section>

        <Section>
          <SectionTitle>PCS ALERT MESSAGE</SectionTitle>
          <InfoGrid>
            <InfoRow style={{ gridColumn: '1 / -1' }}>
              <InfoValue>
                1) Must Order - Bottle Trap<br />
                2) Faucet Recommended - Tall Basin Mixer/Wall Mount Basin Mixer
              </InfoValue>
            </InfoRow>
          </InfoGrid>
        </Section>

        <Section>
          <SectionTitle>CURRENT MRP</SectionTitle>
          <InfoGrid>
            <InfoRow style={{ gridColumn: '1 / -1' }}>
              <InfoValue style={{ fontSize: '16px', fontWeight: '600' }}>
                47000.00
              </InfoValue>
            </InfoRow>
          </InfoGrid>
        </Section>

        <ButtonGroup>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={onShortlist}>
            Shortlist
          </Button>
        </ButtonGroup>
      </Content>

      {selectedImage && (
        <FullScreenImage
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <FullScreenImageContent>
            <FullScreenImageView
              src={productData.images[selectedImageIndex]}
              alt="Full screen product"
              initial={{ x: slideDirection === 'right' ? 100 : -100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: slideDirection === 'right' ? -100 : 100, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            />
            <NavigationControls>
              <NavigationButton onClick={handlePrevImage}>
                <FiChevronLeft />
              </NavigationButton>
              <NavigationButton onClick={handleNextImage}>
                <FiChevronRight />
              </NavigationButton>
            </NavigationControls>
            <FullScreenImageClose onClick={() => setSelectedImage(null)}>
              <FiX />
            </FullScreenImageClose>
          </FullScreenImageContent>
        </FullScreenImage>
      )}
    </ModalOverlay>
  );
};

export default SanitaryProductView; 